import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  deleteNarrativeTemplates,
  readNarrativeTemplates,
  updateNarrativeTemplate,
} from '@api/narratives';
import {
  Button,
  getFormattedLongDate,
  IconButtonV2,
  Input,
  MeatballsIcon,
  ModalV2 as Modal,
  OverlayPopover,
  PencilIcon,
  Popover,
  TableList,
  TextArea,
  toast,
  TrashIcon,
} from '@utilities';

import { getTemplateTopicIds } from '../../../NarrativesChat/utilities/helpers';

import styles from './_index.module.scss';

const hasPersistedTemplateId = (templateId) =>
  templateId !== null &&
  templateId !== undefined &&
  !String(templateId).startsWith('temp-template-');

const TemplateNameCell = ({ onSelectTemplate, row }) => {
  const description = row?.original?.description;

  return (
    <button
      className={styles['custom-templates-name-button']}
      onClick={() => onSelectTemplate?.(row?.original)}
      type="button"
    >
      <div className={styles['custom-templates-name']}>
        <p className={styles['custom-templates-title']}>{row?.original?.name}</p>
        <p>{description || 'No description'}</p>
      </div>
    </button>
  );
};

TemplateNameCell.propTypes = {
  onSelectTemplate: PropTypes.func,
  row: PropTypes.shape({
    original: PropTypes.shape({
      description: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
};

const MetaValueCell = ({ value }) => (
  <div className={styles['custom-templates-meta-value']}>{value}</div>
);

MetaValueCell.propTypes = {
  value: PropTypes.node,
};

const LastUpdatedCell = ({ value }) => (
  <div className={styles['custom-templates-meta-value']}>
    {getFormattedLongDate(new Date(value))}
  </div>
);

LastUpdatedCell.propTypes = {
  value: PropTypes.string,
};

const TemplateActionsCell = ({
  actionButtonRefs,
  activeActionsTemplateId,
  onActionButtonClick,
  onOpenDeleteModal,
  onOpenEditModal,
  row,
}) => {
  const template = row?.original;
  const templateId = template?.id;

  if (!hasPersistedTemplateId(templateId)) return null;

  return (
    <div
      className={styles['custom-templates-actions']}
      onClick={(event) => event.stopPropagation()}
    >
      <IconButtonV2
        aria-label="More Actions"
        className={styles['custom-templates-meatball']}
        data-testid={`template-table-more-actions-${templateId}`}
        icon={<MeatballsIcon />}
        onClick={(event) => onActionButtonClick(event, templateId)}
        ref={(element) => {
          actionButtonRefs.current[templateId] = element;
        }}
        size="small"
        variant="subtle"
      />
      <OverlayPopover
        hasArrow
        isActive={activeActionsTemplateId === templateId}
        onHide={() => onActionButtonClick(null, null)}
        placement="bottom"
        target={actionButtonRefs.current[templateId]}
      >
        <Popover.Menu>
          <Popover.Item
            aria-label="Edit Template Details"
            data-testid={`template-table-edit-${templateId}`}
            icon={<PencilIcon />}
            onClick={(event) => onOpenEditModal(event, template)}
          >
            <p>Edit Details</p>
          </Popover.Item>
          <Popover.Item
            aria-label="Delete Template"
            data-testid={`template-table-delete-${templateId}`}
            icon={<TrashIcon />}
            onClick={(event) => onOpenDeleteModal(event, templateId)}
          >
            <p>Delete</p>
          </Popover.Item>
        </Popover.Menu>
      </OverlayPopover>
    </div>
  );
};

TemplateActionsCell.propTypes = {
  actionButtonRefs: PropTypes.shape({
    current: PropTypes.object,
  }).isRequired,
  activeActionsTemplateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onActionButtonClick: PropTypes.func.isRequired,
  onOpenDeleteModal: PropTypes.func.isRequired,
  onOpenEditModal: PropTypes.func.isRequired,
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  }),
};

const CustomTemplates = ({ onSelectTemplate }) => {
  const [activeActionsTemplateId, setActiveActionsTemplateId] = useState(null);
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [editedName, setEditedName] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [error, setError] = useState('');
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const initialSortBy = useMemo(() => [{ desc: true, id: 'lastUpdated' }], []);
  const actionButtonRefs = useRef({});

  const fetchTemplates = useCallback(async () => {
    try {
      setError('');
      setIsLoading(true);
      const response = await readNarrativeTemplates();
      setTemplates(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error(err);
      setError('Unable to load templates. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleActionButtonClick = useCallback((event, templateId) => {
    event?.stopPropagation();
    setActiveActionsTemplateId((currentId) => (currentId === templateId ? null : templateId));
  }, []);

  const handleOpenEditModal = useCallback((event, template) => {
    event.stopPropagation();
    setActiveActionsTemplateId(null);
    setEditingTemplate(template);
    setEditedDescription(template?.description || '');
    setEditedName(template?.name || '');
  }, []);

  const handleOpenDeleteModal = useCallback((event, templateId) => {
    event.stopPropagation();
    setActiveActionsTemplateId(null);
    setDeleteTemplateId(templateId);
  }, []);

  const handleSaveTemplate = async () => {
    if (!hasPersistedTemplateId(editingTemplate?.id)) return;

    const normalizedDescription = editedDescription.trim();
    const normalizedName = editedName.trim();

    if (!normalizedName) return;

    try {
      setIsSavingTemplate(true);
      await updateNarrativeTemplate({
        description: normalizedDescription,
        name: normalizedName,
        templateId: editingTemplate.id,
        topicIds: getTemplateTopicIds(editingTemplate),
      });

      setTemplates((previousTemplates) =>
        previousTemplates.map((template) =>
          template?.id === editingTemplate.id
            ? {
                ...template,
                description: normalizedDescription,
                name: normalizedName,
              }
            : template
        )
      );
      setEditingTemplate(null);
      toast.success('Template details updated.');
    } catch (error) {
      console.error(error);
      toast.error('Unable to update template. Please try again.');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!hasPersistedTemplateId(deleteTemplateId)) return;

    try {
      setIsDeletingTemplate(true);
      await deleteNarrativeTemplates({ ids: [deleteTemplateId] });
      setTemplates((previousTemplates) =>
        previousTemplates.filter((template) => template?.id !== deleteTemplateId)
      );
      setDeleteTemplateId(null);
      toast.success('Template deleted.');
    } catch (error) {
      console.error(error);
      toast.error('Unable to delete template. Please try again.');
    } finally {
      setIsDeletingTemplate(false);
    }
  };

  const renderEmptyMessage = () => {
    if (error) {
      return <p>{error}</p>;
    }

    return (
      <>
        <h3>No custom templates to show</h3>
        <p>Create a custom template from a Narrative to reuse its structure in future work.</p>
      </>
    );
  };

  const columns = useMemo(
    () => [
      {
        accessor: 'name',
        Cell: (cellProps) => (
          <TemplateNameCell onSelectTemplate={onSelectTemplate} row={cellProps.row} />
        ),
        Header: 'Template Name',
        width: 900,
      },
      {
        accessor: 'creator',
        Cell: MetaValueCell,
        Header: 'Owner',
        width: 120,
      },
      {
        accessor: 'lastUpdated',
        Cell: LastUpdatedCell,
        Header: 'Last Updated',
        width: 120,
      },
      {
        Cell: (cellProps) => (
          <TemplateActionsCell
            actionButtonRefs={actionButtonRefs}
            activeActionsTemplateId={activeActionsTemplateId}
            onActionButtonClick={handleActionButtonClick}
            onOpenDeleteModal={handleOpenDeleteModal}
            onOpenEditModal={handleOpenEditModal}
            row={cellProps.row}
          />
        ),
        disableSortBy: true,
        Header: '',
        id: 'actions',
        width: 40,
      },
    ],
    [
      activeActionsTemplateId,
      handleActionButtonClick,
      handleOpenDeleteModal,
      handleOpenEditModal,
      onSelectTemplate,
    ]
  );

  return (
    <div className={styles['custom-templates']}>
      <TableList
        columns={columns}
        currentPage={1}
        data={templates}
        enableNameCellClick={false}
        enableRowActions={false}
        enableRowExpand={false}
        hasSelectionColumn={false}
        hideTrailingCellsOnHover={false}
        initialSortBy={initialSortBy}
        isLoading={isLoading}
        pageCount={0}
        renderEmptyMessage={renderEmptyMessage}
        rowClassName={styles['custom-templates-row']}
        selectedRows={[]}
        showFirstColumnDivider={false}
        showPagination={false}
      />
      <Modal
        buttons={[
          <Button
            key="cancel-edit-template"
            onClick={() => setEditingTemplate(null)}
            text="Cancel"
            variant="secondary"
          />,
          <Button
            isDisabled={!editedName.trim() || isSavingTemplate}
            key="save-edit-template"
            onClick={handleSaveTemplate}
            text="Save"
          />,
        ]}
        isActive={Boolean(editingTemplate)}
        onClose={() => setEditingTemplate(null)}
        title="Edit template details"
      >
        <div className={styles['narratives-custom-templates-modal-fields']}>
          <Input
            aria-label="Template Name"
            id="edit-template-name"
            label="Template Name"
            onChange={(value) => setEditedName(String(value))}
            value={editedName}
          />
          <TextArea
            aria-label="Template Description"
            id="edit-template-description"
            label="Description"
            onChange={setEditedDescription}
            rows={4}
            value={editedDescription}
          />
        </div>
      </Modal>
      <Modal
        buttons={[
          <Button
            key="cancel-delete-template"
            onClick={() => setDeleteTemplateId(null)}
            text="Cancel"
            variant="secondary"
          />,
          <Button
            isDisabled={isDeletingTemplate}
            key="confirm-delete-template"
            onClick={handleDeleteTemplate}
            text="Delete"
          />,
        ]}
        isActive={hasPersistedTemplateId(deleteTemplateId)}
        onClose={() => setDeleteTemplateId(null)}
        title="Delete template"
      >
        Are you sure you want to delete this template?
      </Modal>
    </div>
  );
};

CustomTemplates.propTypes = {
  onSelectTemplate: PropTypes.func,
};

export default CustomTemplates;
